/**
 * One service's connection: its credentials, settings and standing.
 *
 * Only what changed is sent. A secret left empty keeps the stored one, and a
 * change to any credential clears the verification — the server does that,
 * because a check of the old credentials says nothing about the new ones.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, CheckCircle2, Copy, ShieldCheck, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  Input,
  ReadOnly,
  Switch,
  api,
  currencySymbol,
  formatDateTime,
  formatMoney,
  formatRelative,
  inSentence,
  toast,
  useCan,
} from '@connect/web-shared';

import {
  ProviderFields,
  fieldsValid,
  initialValues,
  requiredFilled,
  type FieldValues,
} from './provider-fields';
import { docPath } from '../docs/docs-page';
import { messagingKeys, type ChannelDef, type ClientChannel, type ProviderDef } from './types';

export function ServiceConnectionCard({
  channel,
  def,
  provider,
  clientName,
  onRemoved,
}: {
  channel: ClientChannel;
  def: ChannelDef;
  provider: ProviderDef;
  clientName: string;
  onRemoved: () => void;
}) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.channel.edit');
  const [credentials, setCredentials] = useState<FieldValues>({});
  const [settings, setSettings] = useState<FieldValues>({});
  const [label, setLabel] = useState('');
  const [active, setActive] = useState(true);
  const [primary, setPrimary] = useState(false);
  const [threshold, setThreshold] = useState('');
  const reportsBalance = provider.capabilities.includes('balance');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setCredentials(initialValues(provider.credentials, channel.credentials));
    setSettings(initialValues(provider.settings, channel.settings));
    setLabel(channel.label ?? '');
    setActive(channel.status === 'active');
    setPrimary(channel.is_primary);
    setThreshold(channel.low_balance_threshold ?? '');
  }, [channel, provider]);

  const refresh = () =>
    void queryClient.invalidateQueries({
      queryKey: messagingKeys.channels(channel.client_public_id),
    });

  const save = useMutation({
    mutationFn: () => {
      const changedCredentials: FieldValues = {};
      for (const spec of provider.credentials) {
        const value = credentials[spec.key] ?? '';
        if (spec.kind === 'secret') {
          if (value) changedCredentials[spec.key] = value;
        } else if (value !== (channel.credentials[spec.key]?.value ?? '')) {
          changedCredentials[spec.key] = value;
        }
      }
      return api.patch<ClientChannel>(`/messaging/channels/${channel.public_id}`, {
        label: label.trim() || null,
        status: active ? 'active' : 'paused',
        is_primary: primary,
        ...(reportsBalance ? { low_balance_threshold: threshold.trim() || null } : {}),
        settings,
        ...(Object.keys(changedCredentials).length ? { credentials: changedCredentials } : {}),
      });
    },
    onSuccess: (saved) => {
      refresh();
      toast.success(
        'Saved',
        saved.verified_at || !provider.capabilities.includes('verify')
          ? undefined
          : 'Verify it to check the credentials.',
      );
    },
  });

  const verify = useMutation({
    mutationFn: () => api.post<ClientChannel>(`/messaging/channels/${channel.public_id}/verify`),
    onSuccess: (checked) => {
      refresh();
      if (checked.last_error) toast.error('The provider refused the credentials', checked.last_error);
      else toast.success('The provider accepted the credentials');
    },
  });

  const error = save.error ?? verify.error;
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;
  const ready =
    requiredFilled(provider.credentials, credentials, channel.credentials) &&
    requiredFilled(provider.settings, settings) &&
    fieldsValid(provider.credentials, credentials) &&
    fieldsValid(provider.settings, settings) &&
    thresholdValid(threshold);

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Connection"
          actions={
            <div className="flex items-center gap-2">
              {provider.guide ? (
                <Can permission="core.doc.view">
                  <Link
                    to={docPath(provider.guide)}
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <BookOpen className="size-3.5" />
                    Setup guide
                  </Link>
                </Can>
              ) : null}
              {canEdit && provider.capabilities.includes('verify') ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={verify.isPending}
                  onClick={() => verify.mutate()}
                >
                  <ShieldCheck className="size-3.5" />
                  Verify
                </Button>
              ) : null}
            </div>
          }
        />
        <CardBody>
          <div className="space-y-5">
            <p className="text-sm text-muted">{provider.description}</p>
  
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
              {channel.verified_at ? (
                <Badge tone="success">
                  <CheckCircle2 className="size-2.5" />
                  Verified {formatDateTime(channel.verified_at)}
                </Badge>
              ) : (
                <Badge tone="warning">Not verified</Badge>
              )}
              {channel.account_status?.balance ? (
                <span>
                  Balance {formatMoney(channel.account_status.balance, channel.account_status.currency ?? 'INR')}
                  {channel.balance_checked_at ? ` · checked ${formatRelative(channel.balance_checked_at)}` : ''}
                </span>
              ) : null}
              {channel.low_balance ? <Badge tone="warning">Low balance</Badge> : null}
            </div>
  
            {channel.vendor ? <VendorLink vendor={channel.vendor} /> : null}
  
            {channel.account_status?.details ? (
              // What the provider said about the account when it was last verified.
              <dl className="grid gap-x-4 gap-y-1 rounded-control border border-line bg-surface-2 p-3 text-xs sm:grid-cols-[auto_1fr]">
                {Object.entries(channel.account_status.details).map(([key, value]) => (
                  <div key={key} className="contents">
                    <dt className="text-muted">{key}</dt>
                    <dd className="text-body">{value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
  
            {channel.last_error ? (
              // The provider's own words when it refused, as written.
              <Alert tone="danger" title="The provider refused these credentials">
                {channel.last_error}
              </Alert>
            ) : null}
  
            {error ? (
              <Alert tone="danger" title="Could not complete that">
                {error instanceof ApiError ? error.message : 'Could not reach the server.'}
              </Alert>
            ) : null}
  
            {channel.webhook ? <WebhookPanel webhook={channel.webhook} providerLabel={provider.label} /> : null}
  
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Credentials</h3>
              <ProviderFields
                specs={provider.credentials}
                values={credentials}
                stored={channel.credentials}
                onChange={setCredentials}
                errors={fieldErrors}
                errorPrefix="credentials"
              />
            </section>
  
            {provider.settings.length ? (
              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Settings</h3>
                <ProviderFields
                  specs={provider.settings}
                  values={settings}
                  onChange={setSettings}
                  errors={fieldErrors}
                  errorPrefix="settings"
                />
              </section>
            ) : null}
  
            <section className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Label"
                value={label}
                maxLength={120}
                placeholder={provider.label}
                onChange={(event) => setLabel(event.target.value)}
              />
              {reportsBalance ? (
                <Input
                  label="Low balance alert"
                  inputMode="decimal"
                  value={threshold}
                  placeholder="Off"
                  prefix={currencySymbol(channel.account_status?.currency ?? 'INR')}
                  error={thresholdValid(threshold) ? undefined : 'A balance, like 500 or 500.00.'}
                  hint={`Checked hourly. ${clientName} and staff are told once when the balance drops below it.`}
                  onChange={(event) => setThreshold(event.target.value)}
                />
              ) : null}
              <div className="space-y-3">
                <Switch
                  label="Sending enabled"
                  checked={active}
                  hint="Off keeps the service configured and refuses every send through it."
                  onChange={(event) => setActive(event.target.checked)}
                />
                <Switch
                  label={`Primary ${def.label} service`}
                  checked={primary}
                  hint={`Sends from ${clientName} use the primary unless told otherwise.`}
                  onChange={(event) => setPrimary(event.target.checked)}
                />
              </div>
            </section>
  
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
              <Can permission="messaging.channel.delete">
                <Button variant="ghost" size="sm" onClick={() => setRemoving(true)}>
                  <Trash2 className="size-3.5" />
                  Remove service
                </Button>
              </Can>
              {canEdit ? (
                <Button
                  size="sm"
                  className="ml-auto"
                  loading={save.isPending}
                  disabled={!ready}
                  onClick={() => save.mutate()}
                >
                  Save changes
                </Button>
              ) : null}
            </div>
          </div>
        </CardBody>
  
        <Confirm
          open={removing}
          onClose={() => setRemoving(false)}
          title="Remove this service?"
          message={`${clientName} will no longer be able to send through ${provider.label}. Its stored credentials, ${inSentence(def.sender_label)}s and templates go with it. The account at the provider is not touched.`}
          confirmLabel="Remove service"
          onConfirm={async () => {
            try {
              await api.delete(`/messaging/channels/${channel.public_id}`);
              refresh();
              onRemoved();
            } catch (caught) {
              toast.error('Could not remove it', caught instanceof ApiError ? caught.message : undefined);
            }
          }}
        />
      </Card>
    </ReadOnly>
  );
}

/** Empty (off) or a non-negative amount with at most two decimals, as the API accepts. */
function thresholdValid(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === '' || /^\d{1,12}(\.\d{1,2})?$/.test(trimmed);
}

/**
 * Where the provider pushes delivery reports, and the token it checks the
 * address with — both pasted into the provider's own settings. Neither is a
 * secret: every report is signed with the connection's app secret, and that is
 * what is trusted. The full address is the API's when the installation knows
 * its public host, else this portal's own: every Connect host proxies the API.
 */
function WebhookPanel({
  webhook,
  providerLabel,
}: {
  webhook: NonNullable<ClientChannel['webhook']>;
  providerLabel: string;
}) {
  const url = webhook.url ?? `${window.location.origin}${webhook.path}`;
  return (
    <section className="space-y-2 rounded-control border border-line p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Delivery webhook</h3>
      <p className="text-xs text-muted">
        {providerLabel} reports what became of each message here. Without it, messages still go and
        stay “Sent”.
      </p>
      <CopyRow label="Callback URL" value={url} />
      <CopyRow label="Verify token" value={webhook.verify_token} />
    </section>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-muted">{label}</span>
      <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 font-mono text-xs text-body" title={value}>
        {value}
      </code>
      <button
        type="button"
        aria-label={`Copy the ${label.toLowerCase()}`}
        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-2xs text-muted hover:bg-surface-2 hover:text-body"
        onClick={() =>
          void navigator.clipboard.writeText(value).then(
            () => setCopied(true),
            () => undefined,
          )
        }
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
  );
}

/**
 * Where the connection sits at the vendor (`docs/31`): the provider account it
 * is a customer of, and its customer id there — or that it is not linked yet,
 * with the way to link it. What is done there is done from Messaging providers.
 */
function VendorLink({ vendor }: { vendor: NonNullable<ClientChannel['vendor']> }) {
  const canSee = useCan('messaging.provider_account.view');
  if (!canSee) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
      {vendor.account ? (
        <>
          <span>
            Customer {vendor.customer_ref} of{' '}
            <Link to={`/messaging/providers/${vendor.account.public_id}?section=clients`} className="text-accent hover:underline">
              {vendor.account.label}
            </Link>
          </span>
          {vendor.profile.active === 'no' ? <Badge tone="warning">Suspended at the vendor</Badge> : null}
          {vendor.profile.rate_per_part ? <span>· rate ₹{vendor.profile.rate_per_part} a part</span> : null}
        </>
      ) : (
        <span>
          Not linked to a provider account.{' '}
          <Link to="/messaging/providers" className="text-accent hover:underline">
            Link it in Messaging providers
          </Link>
        </span>
      )}
    </div>
  );
}
